import React from "react";
import styled from "styled-components";
import tw from "twin.macro";

import logo from "../../images/kc-logo.png";
import whatsappIcon from "images/whatsapp.png";
import googleMapsIcon from "images/google-maps.png";

const Container = styled.div`
  ${tw`relative -mb-8 -mx-8 px-8 py-20 lg:py-24`}
  background: linear-gradient(135deg, #0C2A29 0%, #134544 50%, #1B6158 100%);
  color: #E0F5F3;
`;

const Content = tw.div`max-w-screen-xl mx-auto relative z-10`;

const ColumnsGrid = styled.div`
  ${tw`flex flex-wrap text-center sm:text-left justify-center sm:justify-start md:justify-between`}
  gap: 3rem;
`;

const Column = styled.div`
  ${tw`px-4 sm:px-0`}
  min-width: 200px;
  flex: 1 1 200px;
  max-width: 420px;
`;

const ColumnHeading = styled.h5`
  ${tw`uppercase font-bold text-sm tracking-widest`}
  color: #42C1B7;
  font-family: 'Nunito', sans-serif;
  letter-spacing: 0.12em;
  margin-bottom: 1rem;
`;

const HoursList = styled.ul`
  ${tw`list-none p-0 m-0`}
  li {
    font-family: 'Nunito', sans-serif;
    font-size: 0.875rem;
    line-height: 1.8;
    color: #B3E8E2;
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    &:last-child {
      color: rgba(179, 232, 226, 0.6);
      font-style: italic;
    }
  }
`;

const MapWrapper = styled.div`
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid rgba(66, 193, 183, 0.2);
  iframe {
    display: block;
    width: 100%;
    max-width: 380px;
    height: 220px;
  }
`;

const PhoneLink = styled.a`
  ${tw`inline-flex items-center justify-center font-bold px-8 py-4 rounded-xl no-underline transition duration-300`}
  background: linear-gradient(135deg, #E1609E, #CC3F87);
  color: #fff;
  font-family: 'Nunito', sans-serif;
  font-size: 1.1rem;
  letter-spacing: 0.02em;
  width: 100%;
  max-width: 280px;
  &:hover {
    background: linear-gradient(135deg, #CC3F87, #A82B6F);
    box-shadow: 0 8px 25px rgba(225, 96, 158, 0.4);
    transform: translateY(-1px);
  }
`;

const ContactActions = styled.div`
  ${tw`flex flex-col items-center sm:items-start gap-4 mt-2`}
`;

const IconRow = styled.div`
  ${tw`flex gap-4 mt-2`}
`;

const IconLink = styled.a`
  ${tw`transition duration-300`}
  &:hover {
    opacity: 0.8;
    transform: scale(1.1);
  }
  img {
    width: 44px;
    height: 44px;
    border-radius: 8px;
  }
`;

const Divider = styled.div`
  ${tw`my-12 w-full`}
  height: 1px;
  background: rgba(66, 193, 183, 0.2);
`;

const BottomRow = tw.div`flex flex-col md:flex-row items-center justify-between gap-4`;

const LogoContainer = styled.div`
  ${tw`flex items-center`}
  img {
    width: 36px;
    height: 36px;
    border-radius: 6px;
  }
`;

const LogoText = styled.h5`
  ${tw`ml-3 text-xl font-black tracking-wide m-0`}
  font-family: 'Cormorant Garant', serif;
  color: #E0F5F3;
`;

const Copyright = styled.p`
  ${tw`text-center text-sm mt-0`}
  color: rgba(179, 232, 226, 0.7);
  font-family: 'Nunito', sans-serif;
`;

export default ({ id = "" }) => {
  return (
    <Container id={id}>
      <Content>
        <ColumnsGrid>
          <Column>
            <ColumnHeading>Location</ColumnHeading>
            <MapWrapper>
              <iframe
                title="Kaushal Clinic Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3423.8682811607023!2d75.8304544758903!3d30.89034557450862!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391a83e536c35e9f%3A0xaa3b5d1712b19247!2sKaushal%20Clinic!5e0!3m2!1sen!2sca!4v1691360848822!5m2!1sen!2sca"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </MapWrapper>
          </Column>

          <Column>
            <ColumnHeading>Clinic Hours</ColumnHeading>
            <HoursList>
              <li><span>Monday</span><span>4:00 – 8:00 PM</span></li>
              <li><span>Tuesday</span><span>4:00 – 8:00 PM</span></li>
              <li><span>Wednesday</span><span>4:00 – 8:00 PM</span></li>
              <li><span>Thursday</span><span>4:00 – 8:00 PM</span></li>
              <li><span>Friday</span><span>4:00 – 8:00 PM</span></li>
              <li><span>Saturday</span><span>4:00 – 8:00 PM</span></li>
              <li><span>Sunday</span><span>By Appointment</span></li>
            </HoursList>
          </Column>

          <Column>
            <ColumnHeading>Contact Us</ColumnHeading>
            <ContactActions>
              <PhoneLink href="tel:+919815662600">
                +91-98156-62600
              </PhoneLink>
              <IconRow>
                <IconLink href="https://wa.me/+919815662600" target="_blank" rel="noopener noreferrer" aria-label="Chat on WhatsApp">
                  <img src={whatsappIcon} alt="WhatsApp" />
                </IconLink>
                <IconLink href="https://goo.gl/maps/XAaY2LnbDShaKA3B8" target="_blank" rel="noopener noreferrer" aria-label="Open in Google Maps">
                  <img src={googleMapsIcon} alt="Google Maps" />
                </IconLink>
              </IconRow>
            </ContactActions>
          </Column>
        </ColumnsGrid>

        <Divider />

        <BottomRow>
          <LogoContainer>
            <img src={logo} alt="Kaushal Clinic" />
            <LogoText>Kaushal Clinic</LogoText>
          </LogoContainer>
          <Copyright>&copy; {new Date().getFullYear()} Owned and operated by Dr. Seema Kaushal, Ludhiana.</Copyright>
        </BottomRow>
      </Content>
    </Container>
  );
};
